import React, { useCallback, useEffect, useRef, useState } from "react";
import EventBus from "@/lib/eventBus";
import { Link } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useInView } from "react-intersection-observer";
import { useCurrentAccountId } from "@/store/authContext/authContext";
import { deleteItem, getChats } from "@/lib/serverComm";
import type { Chat } from "@/types/chat";

const Chats: React.FC = () => {
  const currentAccountId = useCurrentAccountId();

  // Local state for chats
  const [chats, setChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  // Parameters for API calls
  const [params, setParams] = useState({
    limit: 20,
    offset: 0,
    query: "",
  });

  const { ref, inView } = useInView({
    threshold: 0.1,
    rootMargin: "100px",
  });

  // Function to load chats
  const loadChats = useCallback(
    async (resetData = false, searchQuery = params.query) => {
      if (loading) return;

      setLoading(true);
      setError(null);

      try {
        const offsetToUse = resetData ? 0 : params.offset;
        const newChats = await getChats(
          params.limit,
          searchQuery,
          offsetToUse,
          undefined, // type
          currentAccountId,
        );

        if (resetData) {
          setChats(newChats);
          setParams((prev) => ({
            ...prev,
            offset: newChats.length,
            query: searchQuery,
          }));
        } else {
          setChats((prev) => [...prev, ...newChats]);
          setParams((prev) => ({
            ...prev,
            offset: prev.offset + newChats.length,
          }));
        }

        // If we got fewer chats than requested, we've reached the end
        setHasMore(newChats.length === params.limit);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load chats");
      } finally {
        setLoading(false);
      }
    },
    [params.limit, params.query, params.offset, currentAccountId, loading],
  );

  const handleDeleteChat = useCallback(async (chatId: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this chat? This action cannot be undone.",
    );
    if (!confirmed) return;

    try {
      await deleteItem("chats", chatId);
      setChats((prev) => prev.filter((chat) => chat.id !== chatId));
      EventBus.$emit("sidebar:refreshChats");
    } catch (err) {
      alert(
        err instanceof Error
          ? `Failed to delete chat: ${err.message}`
          : "Failed to delete chat",
      );
    }
  }, []);

  // Load more when scrolling into view
  useEffect(() => {
    if (inView && hasMore && !loading && chats.length > 0) {
      loadChats(false);
    }
  }, [inView, hasMore, loading, chats.length, loadChats]);

  // Initial load and reload when account changes
  useEffect(() => {
    setChats([]);
    setHasMore(true);

    const loadInitialChats = async () => {
      setLoading(true);
      setError(null);

      try {
        const newChats = await getChats(
          20, // limit
          "", // initial empty query
          0, // offset
          undefined, // type
          currentAccountId,
        );

        setChats(newChats);
        setParams({ limit: 20, offset: newChats.length, query: "" });
        setHasMore(newChats.length === 20);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load chats");
      } finally {
        setLoading(false);
      }
    };

    loadInitialChats();

    // Cleanup debounce timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [currentAccountId]);

  // Debounced search
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();

  const debouncedSearch = useCallback(
    (searchTerm: string) => {
      console.log("Debounced search term:", searchTerm);

      // Clear previous timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new timeout
      debounceTimeoutRef.current = setTimeout(() => {
        setChats([]);
        setHasMore(true);
        loadChats(true, searchTerm);
      }, 300);
    },
    [loadChats],
  );

  return (
    <ScrollArea className="h-[calc(100vh-56px)]">
      <div className="container mx-auto px-4 pt-4 text-gray-800 dark:text-gray-200 max-w-3xl h-full">
        <h1 className="text-3xl font-bold mb-6">Your Chats</h1>
        <input
          type="text"
          placeholder="Search chats..."
          className="w-full p-2 mb-6 border rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          onChange={(e) => debouncedSearch(e.target.value)}
        />

        {renderContent()}
      </div>
    </ScrollArea>
  );

  function renderContent() {
    if (loading && chats.length === 0) {
      return (
        <p className="text-gray-600 dark:text-gray-400">Loading chats...</p>
      );
    }
    if (error) return <p className="text-red-500 dark:text-red-400">{error}</p>;
    if (chats.length === 0) {
      return (
        <p className="text-gray-600 dark:text-gray-400">
          No results found matching your search.
        </p>
      );
    }
    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {chats.map(renderChatLink)}
        </div>
        {hasMore && (
          <div ref={ref} className="h-4 w-full my-4">
            {loading && chats.length > 0 && (
              <p className="text-gray-600 dark:text-gray-400">
                Loading more chats...
              </p>
            )}
          </div>
        )}
      </>
    );
  }

  function renderChatLink(chat: Chat) {
    return (
      <div
        key={chat.id}
        className="relative p-4 border rounded bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-gray-200 dark:border-gray-700"
      >
        <Link to={`/chat/${chat.id}`} className="block">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
            {chat.title}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Last updated: {new Date(chat.updated_at).toLocaleString()}
          </p>
        </Link>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDeleteChat(chat.id);
          }}
          className="absolute bottom-3 right-3 p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          aria-label="Delete chat"
        >
          <Trash2 size={16} />
        </button>
      </div>
    );
  }
};

export default Chats;
